import { Box, Toolbar } from '@mui/material';
import { Handle } from '@remix-run/router';
import { SCROLLABLE_DIV_ID } from 'components/constants/Constants';
import { trackMatomoPage } from 'piwik/piwik';
import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useLocation, useMatches, useOutlet } from 'react-router-dom';

type GenericLayoutRootProps = {
  defaultColor?: string;
  NavigationBarComponent: React.FC<{
    setToolBarHeight: (height: number) => void;
  }>;
  FooterComponent?: React.FC;
  FooterDrawerComponent?: React.FC;
  EventSignupDialogComponent?: React.FC;
};

export const GenericLayoutRoot: React.FC<GenericLayoutRootProps> = props => {
  const outlet = useOutlet();
  const matches = useMatches();
  const handle = matches[matches.length - 1]?.handle as Handle | undefined;
  const { pathname } = useLocation();
  const [loggedPageInMatomo, setLoggedPageInMatomo] = useState(false);
  const { isAuthenticated } = useAuth();
  useEffect(() => {
    if (!loggedPageInMatomo) {
      trackMatomoPage({
        context: location.host,
        isAuthenticated: isAuthenticated,
        where: pathname
      });
    } else {
      setLoggedPageInMatomo(true);
    }
  }, [isAuthenticated, loggedPageInMatomo, pathname]);
  useEffect(() => {
    // -90 is a way to ensure we sroll back to top and also still scroll a bit more upwards to compensate the height of the nav bar
    document.getElementById(SCROLLABLE_DIV_ID)?.scrollTo({ top: -90 });
    // document.documentElement.scrollTo(0, 0);
  }, [pathname]);
  const [toolBarHeight, setToolBarHeight] = useState<number>();

  return (
    <Box
      sx={theme => ({
        backgroundColor:
          handle?.customBackground ??
          props.defaultColor ??
          theme.palette.lightBackground,
        height: '100vh',
        '&': {
          // This fixes the layout for mobile devices where the address bar is taken into the height calculation.
          // We have to keep 100vh as a fallback for browsers that don't support this.
          // But we prioritize 100dvh for browsers that do.
          height: '100dvh'
        },
        display: 'flex',
        flexDirection: 'column',
        ...(handle?.customBackgroundImage
          ? {
              backgroundSize: 'cover',
              backgroundImage: `url(${handle?.customBackgroundImage})`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }
          : {})
      })}
    >
      {!handle?.hideEventSignupDialog && props.EventSignupDialogComponent && (
        <props.EventSignupDialogComponent />
      )}

      <props.NavigationBarComponent setToolBarHeight={setToolBarHeight} />
      <Toolbar
        style={{
          minHeight: toolBarHeight ?? 'auto'
        }} /* acts as a space placeholder with the same height as the NavigationBar */
      />

      <div
        // This div fit perfectly under the NavBar.
        id={SCROLLABLE_DIV_ID}
        style={{
          overflow: 'auto',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          // This box can have an unlimited height. It will be scrollable inside the parent div.
          sx={{
            flexGrow: 1, // moves the footer to the bottom of the page
            display: 'flex', // TODO only for non-scrolling layout
            flexDirection: 'column',
            overflow: handle?.nonScrollablePage ? 'hidden' : undefined
          }}
        >
          {outlet}
        </Box>
        {handle?.showFooter && props.FooterComponent && (
          <props.FooterComponent />
        )}
      </div>
      {/* //has to be here in order to be shown on pages without footer(eg seerchindex) */}
      {props.FooterDrawerComponent && <props.FooterDrawerComponent />}
    </Box>
  );
};
